import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContextType, User } from '../types/auth';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const checkSession = async () => {
    if (sessionChecked) return;
    
    try {
      const response = await authAPI.getSession();
      setUser(response.user);
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
      setSessionChecked(true);
    }
  };

  const refreshSession = async () => {
    try {
      await authAPI.refreshSession();
    } catch (error) {
      console.error('Session refresh failed:', error);
      setUser(null);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (user) {
        refreshSession();
      }
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const login = async (identifier: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authAPI.login(identifier, password);
      setUser(response.user);
      setMustChangePassword(response.mustChangePassword);
      return { mustChangePassword: response.mustChangePassword };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Pålogging feilet';
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setMustChangePassword(false);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
    setIsLoading(true);
    try {
      const response = await authAPI.changePassword(currentPassword, newPassword, confirmPassword);
      setUser(response.user);
      setMustChangePassword(false);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Feil ved endring av passord';
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    mustChangePassword,
    login,
    logout,
    refreshSession,
    changePassword,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};